import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HotPicks = ({ 
  onPlayTrack, 
  onDownloadTrack, 
  onAddToCrate,
  userRatings = {} 
}) => {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('all');
  const [viewMode, setViewMode] = useState('rated'); // 'rated' or 'downloaded'

  const periods = [
    { value: 'today', label: 'Hoy' },
    { value: 'week', label: 'Semana' },
    { value: 'month', label: 'Mes' },
    { value: 'all', label: 'Todo' }
  ];

  useEffect(() => {
    fetchHotPicks();
  }, [period, viewMode]);

  const fetchHotPicks = async () => {
    try {
      setLoading(true);
      const endpoint = viewMode === 'rated' 
        ? `/tracks/top-rated?limit=10&period=${period}`
        : `/tracks/most-downloaded?limit=10&period=${period}`;
      
      const response = await api.get(endpoint, { withCredentials: true });
      setTracks(response.data);
    } catch (error) {
      console.error('Error fetching hot picks:', error);
    } finally {
      setLoading(false);
    }
  };

  const getVersionBadgeColor = (version) => {
    const v = version?.toLowerCase() || '';
    if (v.includes('clean') || v.includes('extended') || v.includes('instrumental')) {
      return 'bg-[#00D9FF]/15 text-[#00D9FF] border-[#00D9FF]/30';
    }
    return 'bg-[#1A1A1A] text-[#888] border-[#333]';
  };

  return (
    <div className="mb-6 animate-fade-in-up">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="w-8 h-8 rounded-lg bg-[#00D9FF]/10 border border-[#00D9FF]/30 flex items-center justify-center">
          <svg className="w-4 h-4 text-[#00D9FF]" fill="currentColor" viewBox="0 0 24 24">
            <path d="M13.5.67s.74 2.65.74 4.8c0 2.06-1.35 3.73-3.41 3.73-2.07 0-3.63-1.67-3.63-3.73l.03-.36C5.21 7.51 4 10.62 4 14c0 4.42 3.58 8 8 8s8-3.58 8-8C20 8.61 17.41 3.8 13.5.67zM11.71 19c-1.78 0-3.22-1.4-3.22-3.14 0-1.62 1.05-2.76 2.81-3.12 1.77-.36 3.6-1.21 4.62-2.58.39 1.29.59 2.65.59 4.04 0 2.65-2.15 4.8-4.8 4.8z"/>
          </svg>
        </div>
        <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Hot Picks</h2>
        
        {/* View Mode Toggle */}
        <div className="flex bg-[#0D0D0D] rounded-lg p-1 ml-auto border border-[#1A1A1A]">
          <button
            onClick={() => setViewMode('rated')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
              viewMode === 'rated' 
                ? 'bg-[#00D9FF] text-black' 
                : 'text-[#666] hover:text-white'
            }`}
          >
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
            </svg>
            Mejor Valorados
          </button>
          <button
            onClick={() => setViewMode('downloaded')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
              viewMode === 'downloaded' 
                ? 'bg-[#00D9FF] text-black' 
                : 'text-[#666] hover:text-white'
            }`}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
            </svg>
            Más Descargados
          </button>
        </div>
      </div>

      {/* Period Filter */}
      <div className="flex flex-wrap gap-2 mb-4">
        {periods.map((p) => (
          <button
            key={p.value}
            onClick={() => setPeriod(p.value)}
            className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-all ${
              period === p.value
                ? 'bg-[#00D9FF] text-black'
                : 'bg-[#141414] text-[#666] hover:text-white hover:bg-[#1A1A1A] border border-[#1A1A1A]'
            }`}
            data-testid={`period-${p.value}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Tracks Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#00D9FF]"></div>
        </div>
      ) : tracks.length === 0 ? (
        <div className="bg-[#0D0D0D] border border-[#1A1A1A] rounded-xl py-8 text-center">
          <svg className="w-8 h-8 text-[#333] mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path d="M3 3v18h18"/><path d="M18 17V9M13 17V5M8 17v-3"/></svg>
          <p className="text-[#666]">No hay datos para este período</p>
          <p className="text-[#444] text-xs mt-1">
            {viewMode === 'rated' 
              ? 'Los tracks necesitan calificaciones para aparecer aquí'
              : 'Los tracks necesitan descargas para aparecer aquí'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {tracks.map((track, index) => (
            <div 
              key={track.track_id} 
              className="bg-[#0D0D0D] border border-[#1A1A1A] rounded-xl p-3 hover:bg-[#141414] hover:border-[#262626] transition-all group"
              data-testid={`hot-pick-${track.track_id}`}
            >
              <div className="flex items-center gap-3">
                {/* Rank */}
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                  index === 0 ? 'bg-[#00D9FF] text-black' :
                  index === 1 ? 'bg-[#00D9FF]/40 text-white border border-[#00D9FF]/40' :
                  index === 2 ? 'bg-[#00D9FF]/15 text-[#00D9FF] border border-[#00D9FF]/30' :
                  'bg-[#1A1A1A] text-[#666]'
                }`}>
                  {index + 1}
                </div>

                {/* Play Button */}
                <button
                  onClick={() => onPlayTrack(track)}
                  className="w-8 h-8 rounded-full bg-[#00D9FF] hover:scale-110 flex items-center justify-center flex-shrink-0 transition-transform"
                  data-testid={`play-hot-${track.track_id}`}
                >
                  <svg className="w-3 h-3 text-black ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                </button>

                {/* Track Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-white text-sm truncate">{track.title}</h3>
                    {track.version && (
                      <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${getVersionBadgeColor(track.version)}`}>
                        {track.version}
                      </span>
                    )}
                  </div>
                  <p className="text-[#666] text-xs truncate">{track.artist}</p>
                  
                  {/* Rating Stars */}
                  {track.average_rating > 0 && (
                    <div className="flex items-center gap-1 mt-1">
                      <div className="flex">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <span 
                            key={star} 
                            className={`text-[10px] ${star <= Math.round(track.average_rating) ? 'text-[#00D9FF]' : 'text-[#333]'}`}
                          >
                            ★
                          </span>
                        ))}
                      </div>
                      <span className="text-[10px] text-[#666]">{track.average_rating?.toFixed(1)} ({track.total_ratings || 0})</span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => onDownloadTrack(track)}
                    className="w-7 h-7 rounded-lg bg-[#00D9FF]/20 hover:bg-[#00D9FF]/40 flex items-center justify-center text-[#00D9FF] transition-all"
                    title="Descargar"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                  </button>
                  <button
                    onClick={() => onAddToCrate(track.track_id)}
                    className="w-7 h-7 rounded-lg bg-[#1A1A1A] hover:bg-[#262626] flex items-center justify-center text-[#666] hover:text-[#00D9FF] transition-all"
                    title="Agregar a Crate"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default HotPicks;
